/**
 * Stations in space from the strategic station list (plan I5; spec §3.4 seams S-M3-5 and S-M2-5, §4.3, §4.4).
 *
 * M3's layout already places the stations the strategic core adopted (same ids). The **added** M4 stations (a
 * `SystemEconomyView` / `LocalManifest` station with no M3 site) become synthetic M3 `StationSite`s and go through
 * the same path as M3's own: the session lists them after M3's sites (`ReachSession.addStationSites`), so the
 * structures stream and draw them, and the M2 bridge's station hook turns each into an M2 station definition, so
 * they dock, trade and repair like any M3 station.
 *
 * - **Synthetic site:** id, name, owner and model from the view; position from the view; the bay faces the star;
 *   type `outpost` (never one of M2's home types, which the bridge would move onto M2's home stations); M2's pricing
 *   role and specialty by template; services: market always, repair at yards and habs. Hidden, non-dockable
 *   stations (M4's raider bases, drawn by M3 at their POI) are left out, and so are M7's reverted restarts
 *   (`isRevertedHulk`: unpowered and undockable; the derelict shows instead, S43).
 * - **Owner changes (M5):** every station whose owner's look differs from its site's gets `setStationLook`, which
 *   changes the drawn look in place (nothing respawns).
 * - **Disabled stations and claim beacons (M5, plan C15):** each sync also dims the disabled stations' lights
 *   (`charter/disabledStations.ts`) and places, re-liveries or removes the system's claim beacon
 *   (`charter/beacons.ts`); both use optional session hooks and skip with one log line without them.
 * - **Megaprojects (LV WK-1v, D5; D-1081):** `megaprojectSpecs` places one spire placeholder (D-052: plinth, tapered stage
 *   blocks, spine and cap, `spireParts`, in the owner's livery) beside each anchor station
 *   with built stages, plus the ghosted next block while a stage builds; megaproject sites are not construction sites.
 * - **Stations under construction** (`SiteView`s) are described by `constructionSites` (their station model at
 *   40% scale, work lights, no docking) and drawn by `stationsFeature.ts` (which also installs all of this).
 * - Without the strategic service nothing here runs, so M3's stand-in layout is untouched.
 *
 * Runs on manifests and system views (rare events); allocates freely.
 */
import { z } from 'zod'
import { logger } from '../core/logger'
import constructionJson from '../data/strategic/constructionViews.json'
import templatesJson from '../data/strategic/templates.json'
import { loadFactionsConfig, type FactionsConfig } from '../galaxy/layout/config'
import { quatLookNegZ } from '../galaxy/layout/frame'
import type { FactionKey, M2Role, QuatT, StationService, StationSite, SystemLayout, Vec3T } from '../galaxy/layout/types'
import type { FactionId as LookId } from '../render/contracts'
import type { StrategicClient } from '../strategic/client'
import { PLAYER_FACTION_ID, type CorpFactionId, type SiteView, type StationView } from '../strategic/types'
import { syncClaimBeacon } from '../charter/beacons'
import { applyDisabledLooks } from '../charter/disabledStations'
import { familyGrammar, MEGA_KINDS, MEGA_MAKERS, type MegaKind, type MegaMaker } from '../megaproject/grammar'

/** The station type every added station gets: `outpost`-like services, never an M2 home type (see the header). */
export const ADDED_STATION_TYPE = 'outpost'
/**
 * How stations under construction are drawn (`src/data/strategic/constructionViews.json`, Zod-validated): `scale`
 * is the fraction of the station model's size (spec §6.5, plan I5: 0.4); the work lights are small unlit markers
 * at the model's upper corners, `sizeM` across in world metres, blinking on for `onFraction` of `blinkPeriodS`.
 */
export const constructionViewsConfigSchema = z
  .object({
    scale: z.number().positive().max(1),
    workLights: z
      .object({
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        sizeM: z.number().positive(),
        blinkPeriodS: z.number().positive(),
        onFraction: z.number().min(0).max(1),
      })
      .strict(),
    /**
     * LV WK-1v (D5, D-052; upgraded by D-1081): the megaproject placeholder beside its anchor station: `offsetM` from the
     * station along world +x. A broad `base` plinth (`widthRatio` × the first block's width, `heightM` tall, its top
     * `topRatio` × its foot), then one tapered `blockM` [w, h, d] block per built stage (block k's foot is `taper`^k ×
     * `blockM`, its top `taper`^(k+1)), a solid central `spine` (`widthRatio` × the first block's width, rising to
     * `heightRatio` × the blocks' stacked height above the plinth, its top `topRatio` × its foot) and a `cap` crown on
     * the spine (`widthRatio` × the spine's top, `heightM` tall, narrowing to `tipRatio`). `sides`: the faces of every
     * part (4 = square frustums, 8 = octagonal). Coloured by the owner's livery (`render/materials/materials.json`: plinth
     * and spine secondary, blocks primary, cap accent); `color` is the ghosted block a stage is building, at
     * `ghostOpacity` with work lights. Absent = not drawn. `assetId` (D-1114): the spire's model on the asset contract
     * (`assets-src/structure.megaproject_spire`, `assetTiers` tiers), drawn in the owner's look at every stage once loaded,
     * cut to the built tiers with the spine lowered (D-1119 session 2); not loaded or absent = the block mesh.
     */
    megaproject: z
      .object({
        offsetM: z.number().positive(),
        assetId: z.string().min(1).optional(),
        /** The asset model's tier count (its stages are cut from it on the CPU; D-1119 spire asset session 2). */
        assetTiers: z.number().int().positive().default(5),
        blockM: z.tuple([z.number().positive(), z.number().positive(), z.number().positive()]),
        taper: z.number().positive().max(1),
        sides: z.number().int().min(3).max(16),
        base: z.object({ widthRatio: z.number().min(1), heightM: z.number().positive(), topRatio: z.number().positive().max(1) }).strict(),
        spine: z.object({ widthRatio: z.number().positive().max(1), heightRatio: z.number().min(1), topRatio: z.number().positive().max(1) }).strict(),
        cap: z.object({ widthRatio: z.number().positive(), heightM: z.number().positive(), tipRatio: z.number().min(0).max(1) }).strict(),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        ghostOpacity: z.number().min(0).max(1),
        /**
         * D-1128 (megaproject recs §6 step 3): a founded megaproject (stored kind and maker, `StationView.megaForm`) draws its
         * grammar placeholder (`megaproject/grammar`, `megaproject/mesh.ts`) instead of the spire: its foot `distanceM`
         * [min, max] from the anchor at a seeded bearing in the disc plane, clear of every dock corridor (`corridorRadiusM`
         * out to `corridorLengthM` along each bay axis) and of the system's other megaprojects by `clearanceM`
         * (`tries` seeded candidates). `relicColor`: First Rush relic parts under every owner. Absent = the spire for all.
         */
        grammar: z
          .object({
            distanceM: z.tuple([z.number().positive(), z.number().positive()]),
            corridorLengthM: z.number().positive(),
            corridorRadiusM: z.number().positive(),
            clearanceM: z.number().nonnegative(),
            tries: z.number().int().positive(),
            relicColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
export type ConstructionViewsConfig = z.infer<typeof constructionViewsConfigSchema>

export function loadConstructionViewsConfig(raw: unknown = constructionJson): ConstructionViewsConfig {
  return constructionViewsConfigSchema.parse(raw)
}

/** A station under construction shows at this fraction of its model's size (spec §6.5, plan I5). */
export const CONSTRUCTION_SCALE = loadConstructionViewsConfig().scale

/** M2's pricing template per strategic template (M2 has only hub, refinery and scrapyard). */
const M2_ROLE: Readonly<Record<string, M2Role>> = {
  refinery: 'refinery',
  ice_works: 'refinery',
  scrapyard: 'scrapyard',
}
/** What an added station's market sells (the bridge's specialties); the rest sells nothing of M2's own. */
const SPECIALTY: Readonly<Record<string, string>> = {
  refinery: 'ores',
  ice_works: 'ice',
}
/** Templates whose stations repair (plan I5: yards and habs). */
const REPAIRS: ReadonlySet<string> = new Set(['yards', 'hab'])

interface TemplateInfo {
  readonly assetId: string
  readonly dockable: boolean
  readonly hidden: boolean
}
const TEMPLATES: ReadonlyMap<string, TemplateInfo> = new Map(
  templatesJson.templates.map((t): [string, TemplateInfo] => [t.id, { assetId: t.assetId, dockable: t.dockable, hidden: t.hidden }]),
)

/**
 * The M3 faction a station of `owner` carries (a station owner outside it gets no synthetic site). The player
 * corporation (`'player'`) carries M3's `corporation.siteFaction` stand-in (M2's station faction).
 */
function factionKeyOf(owner: CorpFactionId, factions: FactionsConfig): FactionKey | null {
  if (owner === PLAYER_FACTION_ID) return factions.corporation?.siteFaction ?? null
  return Object.hasOwn(factions.factions, owner) ? (owner as FactionKey) : null
}

/** The render look of a strategic owner: the corporation look for `'player'`, null for an owner M3 does not list. */
export function lookOf(owner: CorpFactionId, factions: FactionsConfig): LookId | null {
  if (owner === PLAYER_FACTION_ID) return factions.corporation?.look ?? null
  const key = factionKeyOf(owner, factions)
  return key === null ? null : factions.factions[key].look
}

/** A stable 32-bit seed of a station id (FNV-1a): the bridge's comms seed. */
export function stationSeed(id: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** The rotation whose bay axis (local −Z) points at the star (the system origin); +Z for a station at the origin. */
export function facingStar(pos: Vec3T): QuatT {
  const d = Math.hypot(pos[0], pos[1], pos[2])
  return d > 0 ? quatLookNegZ([-pos[0], -pos[1], -pos[2]]) : quatLookNegZ([0, 0, -1])
}

/**
 * LV WK-1v (lean fix): a yaw-only turn toward the star, so local +Y is exactly the system's up (world +Y, `frame.ts`).
 * `facingStar` pitches toward the star when the position sits off the disc plane, which leaned the megaproject spire;
 * a tall structure stands on the system's up instead. On the y axis (no horizontal direction) it is the identity.
 */
export function uprightFacingStar(pos: Vec3T): QuatT {
  return Math.hypot(pos[0], pos[2]) > 0 ? quatLookNegZ([-pos[0], 0, -pos[2]]) : [0, 0, 0, 1]
}

/** The services of an added station: market always, repair at yards and habs. */
export function servicesOf(template: string): StationService[] {
  return REPAIRS.has(template) ? ['market', 'repair'] : ['market']
}

/**
 * A reverted restart's station (M7 S23/S43): M4 never frees a station, so a revert leaves it in the store as an
 * unpowered (disabled by its own owner, `war.ts` `unpowered`), undockable hulk. It is never drawn, lit, re-liveried
 * or given a claim beacon: the derelict, and any re-restart at the same spot, shows instead.
 */
export function isRevertedHulk(view: Pick<StationView, 'dockable' | 'disabledBy' | 'owner'>): boolean {
  return !view.dockable && view.disabledBy !== null && view.disabledBy === view.owner
}

/**
 * The synthetic M3 site of an added station, or null when it is not drawn as a station (hidden or not dockable, or
 * an owner outside M3's factions). `index` is its place in the system's list (the session re-indexes it anyway).
 */
export function synthStationSite(view: StationView, index: number, factions: FactionsConfig): StationSite | null {
  const t = TEMPLATES.get(view.template)
  if (!view.dockable || t?.hidden === true || isRevertedHulk(view)) return null
  const faction = factionKeyOf(view.owner, factions)
  const look = lookOf(view.owner, factions)
  if (faction === null || look === null) {
    logger.warn(`strategic stations: ${view.id} has owner ${view.owner}, which the M3 factions do not list; not drawn`)
    return null
  }
  const position: Vec3T = [view.pos[0], view.pos[1], view.pos[2]]
  return {
    id: view.id,
    index,
    name: view.name,
    type: ADDED_STATION_TYPE,
    faction,
    look,
    assetId: view.assetId,
    position,
    rotation: facingStar(position),
    services: servicesOf(view.template),
    m2Role: M2_ROLE[view.template] ?? 'hub',
    specialty: SPECIALTY[view.template] ?? null,
    priceSeed: stationSeed(view.id),
    anchor: { kind: 'free', id: null },
  }
}

/** The synthetic sites of the view's stations that M3's layout does not place (`m3Ids`), in view order. */
export function addedStationSites(stations: readonly StationView[], m3Ids: ReadonlySet<string>, firstIndex: number, factions: FactionsConfig): StationSite[] {
  const out: StationSite[] = []
  for (const v of stations) {
    if (m3Ids.has(v.id)) continue
    const site = synthStationSite(v, firstIndex + out.length, factions)
    if (site !== null) out.push(site)
  }
  return out
}

/** A station under construction as drawn: its model at `CONSTRUCTION_SCALE`, work lights on, never dockable. */
export interface ConstructionSiteSpec {
  readonly id: string
  readonly assetId: string
  readonly look: LookId
  readonly position: Vec3T
  readonly rotation: QuatT
  readonly scale: number
  readonly workLights: true
  readonly dockable: false
}

/** The drawn construction sites of a system (sites of hidden templates or unknown owners are left out). */
export function constructionSites(sites: readonly SiteView[], factions: FactionsConfig, scale: number = CONSTRUCTION_SCALE): ConstructionSiteSpec[] {
  const out: ConstructionSiteSpec[] = []
  for (const s of sites) {
    // LV WK-1v (review m10): a megaproject stage is an upgrade-shaped site on its anchor station, so its 40 % model would
    // sit inside the station; it shows as the next ghosted block of the megaproject instead (`megaprojectSpecs`).
    if (s.works === 'megaproject') continue
    const t = TEMPLATES.get(s.template)
    const look = lookOf(s.owner, factions)
    if (t === undefined || t.hidden || look === null) continue
    const position: Vec3T = [s.pos[0], s.pos[1], s.pos[2]]
    out.push({ id: s.id, assetId: t.assetId, look, position, rotation: facingStar(position), scale, workLights: true, dockable: false })
  }
  return out
}

/** LV WK-1v: one megaproject as drawn: `stage` solid blocks beside its anchor station, plus a ghosted block while one builds. */
export interface MegaprojectSpec {
  /** The anchor station's id. */
  readonly id: string
  readonly look: LookId
  readonly position: Vec3T
  /** Yaw only (`uprightFacingStar`): the spire stands on the system's up, never leaning with the star direction. */
  readonly rotation: QuatT
  /** Built stages (solid blocks). */
  readonly stage: number
  readonly of: number
  /** A stage is under construction: the next block shows ghosted, with work lights. */
  readonly building: boolean
  /**
   * D-1128: the founded megaproject's form (a known grammar family): drawn as its grammar placeholder, placed by the
   * feature (`megaPlacement.ts`); absent = the spire (unfounded or legacy, no stored kind).
   */
  readonly form?: MegaFormSpec
}

/** D-1128: a megaproject's grammar inputs (`StationView.megaForm`, checked against the grammar families). */
export interface MegaFormSpec {
  readonly kind: MegaKind
  readonly maker: MegaMaker
  readonly seed: number
}

/** The grammar form of a station's `megaForm`, or undefined when it names no grammar family (the spire then). */
export function megaFormSpecOf(v: Pick<StationView, 'megaForm'>): MegaFormSpec | undefined {
  const f = v.megaForm
  if (f === undefined) return undefined
  const kind = MEGA_KINDS.find((k) => k === f.kind)
  const maker = MEGA_MAKERS.find((m) => m === f.maker)
  if (kind === undefined || maker === undefined || familyGrammar(kind, maker) === undefined) return undefined
  return { kind, maker, seed: f.seed >>> 0 }
}

/**
 * The megaprojects of a system (LV WK-1v, D5): one per station with `megaStage` > 0 or a live megaproject site, beside
 * it at `offsetM`; nothing at stage 0 without a live site (the bite: a stage-0 station draws nothing). Hidden templates
 * and unknown owners are left out. D-1128: with `grammar` on, a station with a founded form (`megaForm`) gets a grammar
 * spec at its anchor (`form` set, upright), which the feature places 4–8 km out (`megaPlacement.ts`).
 */
export function megaprojectSpecs(stations: readonly StationView[], sites: readonly SiteView[], factions: FactionsConfig, offsetM: number, grammar = true): MegaprojectSpec[] {
  const building = new Set<string>()
  for (const s of sites) if (s.works === 'megaproject' && s.stationId !== undefined) building.add(s.stationId)
  const out: MegaprojectSpec[] = []
  for (const v of stations) {
    const stage = v.megaStage?.stage ?? 0
    const live = building.has(v.id)
    if (stage <= 0 && !live) continue
    const t = TEMPLATES.get(v.template)
    const look = lookOf(v.owner, factions)
    if (t === undefined || t.hidden || look === null || isRevertedHulk(v)) continue
    const form = grammar ? megaFormSpecOf(v) : undefined
    const of = v.megaStage?.of ?? stage + 1
    if (form !== undefined) {
      // The grammar placeholder: at the anchor until the feature places it (megaPlacement.ts); upright, no yaw.
      out.push({ id: v.id, look, position: [v.pos[0], v.pos[1], v.pos[2]], rotation: [0, 0, 0, 1], stage, of, building: live, form })
      continue
    }
    const position: Vec3T = [v.pos[0] + offsetM, v.pos[1], v.pos[2]]
    out.push({ id: v.id, look, position, rotation: uprightFacingStar(position), stage, of, building: live })
  }
  return out
}

/** One block of a megaproject spire, model-local: centre height `y` and its size (at its foot). */
export interface SpireBlock {
  readonly y: number
  readonly w: number
  readonly h: number
  readonly d: number
}

/**
 * The first `count` blocks of a megaproject spire (LV WK-1v): block `k` is `blockM` × `taper`^k, stacked on the one
 * below from `y0` up (the plinth's top, D-1081). Stage `n` draws blocks 0…n − 1; the ghost of a building stage is
 * block `n`.
 */
export function spireBlocks(count: number, blockM: readonly [number, number, number], taper: number, y0 = 0): SpireBlock[] {
  const out: SpireBlock[] = []
  let base = y0
  let f = 1
  for (let k = 0; k < count; k++) {
    const h = blockM[1] * f
    out.push({ y: base + h / 2, w: blockM[0] * f, h, d: blockM[2] * f })
    base += h
    f *= taper
  }
  return out
}

/** The livery slot a spire part takes (`render/materials/materials.json` faction palette). */
export type SpireSlot = 'paintPrimary' | 'paintSecondary' | 'paintAccent'

/**
 * One solid part of a megaproject spire (D-1081), model-local: a `sides`-faced frustum centred at height `y`, `h`
 * tall, `w` × `d` across the flats at its foot and `top` × that at its top, coloured by the owner's `slot`.
 */
export interface SpirePart {
  readonly kind: 'base' | 'block' | 'spine' | 'cap'
  readonly y: number
  readonly w: number
  readonly d: number
  readonly h: number
  readonly top: number
  readonly slot: SpireSlot
}

type MegaprojectConfig = NonNullable<ConstructionViewsConfig['megaproject']>

/**
 * The solid parts of a spire with `count` built stages (D-1081, a D-052 placeholder): the broad plinth, then the
 * stage blocks tapering up from its top, then (from stage 1) the central spine rising past the top block and its cap.
 * Count 0 is the plinth alone (a stage-1 build's foundation). Every part is solid; nothing is a lattice.
 */
export function spireParts(count: number, mc: MegaprojectConfig): SpirePart[] {
  const [bw, , bd] = mc.blockM
  const b = mc.base
  const out: SpirePart[] = [
    { kind: 'base', y: b.heightM / 2, w: bw * b.widthRatio, d: bd * b.widthRatio, h: b.heightM, top: b.topRatio, slot: 'paintSecondary' },
  ]
  if (count <= 0) return out
  let stack = 0
  for (const k of spireBlocks(count, mc.blockM, mc.taper, b.heightM)) {
    out.push({ kind: 'block', y: k.y, w: k.w, d: k.d, h: k.h, top: mc.taper, slot: 'paintPrimary' })
    stack += k.h
  }
  const sp = mc.spine
  const spineH = stack * sp.heightRatio
  const sw = bw * sp.widthRatio
  const sd = bd * sp.widthRatio
  out.push({ kind: 'spine', y: b.heightM + spineH / 2, w: sw, d: sd, h: spineH, top: sp.topRatio, slot: 'paintSecondary' })
  const c = mc.cap
  out.push({
    kind: 'cap',
    y: b.heightM + spineH + c.heightM / 2,
    w: sw * sp.topRatio * c.widthRatio,
    d: sd * sp.topRatio * c.widthRatio,
    h: c.heightM,
    top: c.tipRatio,
    slot: 'paintAccent',
  })
  return out
}

/** The session calls I5 needs (M3's `ReachSession`, additive). */
export interface StationSiteHost {
  readonly layout: SystemLayout | null
  addStationSites(systemId: string, sites: readonly StationSite[]): Promise<void>
  setStationLook(systemId: string, siteId: string, look: LookId): void
}

/** Whether `session` has I5's hooks (M3's session does; a stand-in session may not). */
export function isStationSiteHost(session: object): session is StationSiteHost {
  const s = session as Partial<StationSiteHost>
  return typeof s.addStationSites === 'function' && typeof s.setStationLook === 'function'
}

export interface StrategicStationsDeps {
  readonly client: Pick<StrategicClient, 'onManifest' | 'onSystemView' | 'manifest'>
  /** The Reach session (`runtime.session`). */
  readonly session: object
  /** M3's own layout of a system (`runtime.layoutOf`, without the added stations). */
  layoutOf(systemId: string): Pick<SystemLayout, 'stations'>
  /** M3's factions (default `factions.json`). */
  readonly factions?: FactionsConfig
}

/**
 * Applies one system's strategic stations to the session: the added sites, then every owner's look, the disabled
 * stations' lights and the claim beacon (M5).
 */
export function syncSystemStations(
  host: StationSiteHost,
  systemId: string,
  stations: readonly StationView[],
  m3: Pick<SystemLayout, 'stations'>,
  factions: FactionsConfig,
): Promise<void> {
  const m3Ids = new Set(m3.stations.map((s) => s.id))
  // M7 S43: reverted hulks are left out of every pass (see `isRevertedHulk`).
  const shown = stations.some(isRevertedHulk) ? stations.filter((v) => !isRevertedHulk(v)) : stations
  const added = host.addStationSites(systemId, addedStationSites(shown, m3Ids, m3.stations.length, factions))
  for (const v of shown) {
    const look = lookOf(v.owner, factions)
    if (look !== null) host.setStationLook(systemId, v.id, look)
  }
  applyDisabledLooks(host, systemId, shown)
  syncClaimBeacon(host, systemId, shown, factions)
  return added
}

/**
 * Keeps the Reach session's stations in step with the strategic core: each manifest (a system entered) and each
 * system view (owners change, stations finish building) re-syncs its system. Returns the unsubscribe. Does nothing
 * (and returns a no-op) when the session lacks the hooks.
 */
export function installStrategicStations(deps: StrategicStationsDeps): () => void {
  const host = deps.session
  if (!isStationSiteHost(host)) {
    logger.warn('strategic stations: the Reach session has no addStationSites; the added stations are not drawn')
    return () => undefined
  }
  const factions = deps.factions ?? loadFactionsConfig()
  const sync = (systemId: string, stations: readonly StationView[]): void => {
    let m3: Pick<SystemLayout, 'stations'>
    try {
      m3 = deps.layoutOf(systemId)
    } catch (err: unknown) {
      logger.error(`strategic stations: no M3 layout for ${systemId}`, err)
      return
    }
    syncSystemStations(host, systemId, stations, m3, factions).catch((err: unknown) =>
      logger.error(`strategic stations: adding the stations of ${systemId} failed`, err),
    )
  }
  const offManifest = deps.client.onManifest((m) => sync(m.systemId, m.stations))
  const offView = deps.client.onSystemView((v) => sync(v.systemId, v.stations))
  const current = deps.client.manifest
  if (current !== null) sync(current.systemId, current.stations)
  return () => {
    offManifest()
    offView()
  }
}
